/**
 * Traveler photo moderation. Photos live in a private bucket; images shown
 * here use short-lived signed URLs. Nothing is public until approved.
 */
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Images, Loader2, Trash2, X } from "lucide-react";

import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  listTravelerPhotos,
  moderateTravelerPhoto,
} from "@/lib/audio-guides-admin.functions";

export const Route = createFileRoute("/_authenticated/admin/fotos-viajeros")({
  head: () => ({
    meta: [
      { title: "Fotos de viajeros · Moderación" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: Page,
});

type Status = "pending" | "approved" | "rejected";

function Page() {
  const qc = useQueryClient();
  const listFn = useServerFn(listTravelerPhotos);
  const moderateFn = useServerFn(moderateTravelerPhoto);
  const [status, setStatus] = useState<Status>("pending");
  const [error, setError] = useState<string | null>(null);

  const photos = useQuery({
    queryKey: ["admin", "traveler-photos", status],
    queryFn: () => listFn({ data: { status, limit: 60 } }),
  });

  const moderate = useMutation({
    mutationFn: (input: { photoId: string; action: "approve" | "reject" | "delete" }) =>
      moderateFn({ data: input }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["admin", "traveler-photos"] });
      void qc.invalidateQueries({ queryKey: ["admin", "audioguides", "stats"] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Acción no completada"),
  });

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-8">
      <AdminNav />

      <header className="mt-6">
        <h1 className="flex items-center gap-2 text-2xl font-black text-foreground">
          <Images className="h-6 w-6 text-primary" />
          Fotos de viajeros
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Cada envío incluye el consentimiento aceptado por el viajero. Solo las fotos aprobadas
          pueden usarse en materiales de Mostrando México.
        </p>
      </header>

      <div className="mt-5 flex gap-2">
        {(["pending", "approved", "rejected"] as Status[]).map((s) => (
          <Button
            key={s}
            size="sm"
            variant={status === s ? "default" : "outline"}
            onClick={() => setStatus(s)}
          >
            {s === "pending" ? "Pendientes" : s === "approved" ? "Aprobadas" : "Rechazadas"}
          </Button>
        ))}
      </div>

      {error && <p className="mt-4 text-sm text-destructive">{error}</p>}

      {photos.isLoading ? (
        <p className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando fotos…
        </p>
      ) : (
        <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(photos.data?.photos ?? []).map((p) => (
            <li key={p.id} className="overflow-hidden rounded-2xl border border-border bg-card">
              {p.url ? (
                <img
                  src={p.url}
                  alt={p.submitterName ?? "Foto de viajero"}
                  className="h-48 w-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-48 items-center justify-center text-xs text-muted-foreground">
                  Vista previa no disponible
                </div>
              )}
              <div className="space-y-2 p-4">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge variant="outline">{p.tourSlug ?? "sin tour"}</Badge>
                  {p.otaChannel && <Badge variant="secondary">{p.otaChannel}</Badge>}
                  <Badge>{p.status}</Badge>
                </div>
                <p className="text-sm font-medium text-foreground">
                  {p.submitterName ?? "Anónimo"}
                </p>
                {p.submitterEmail && (
                  <p className="truncate text-xs text-muted-foreground">{p.submitterEmail}</p>
                )}
                <p className="text-[11px] text-muted-foreground">
                  Consentimiento {p.consentVersion} ·{" "}
                  {new Date(p.consentAcceptedAt).toLocaleString("es-MX")}
                </p>
                <details className="text-[11px] text-muted-foreground">
                  <summary className="cursor-pointer">Ver texto aceptado</summary>
                  <p className="mt-1 whitespace-pre-wrap">{p.consentText}</p>
                </details>
                <div className="flex gap-2 pt-1">
                  {p.status !== "approved" && (
                    <Button
                      size="sm"
                      disabled={moderate.isPending}
                      onClick={() => moderate.mutate({ photoId: p.id, action: "approve" })}
                    >
                      <Check className="mr-1 h-4 w-4" /> Aprobar
                    </Button>
                  )}
                  {p.status !== "rejected" && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={moderate.isPending}
                      onClick={() => moderate.mutate({ photoId: p.id, action: "reject" })}
                    >
                      <X className="mr-1 h-4 w-4" /> Rechazar
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={moderate.isPending}
                    onClick={() => moderate.mutate({ photoId: p.id, action: "delete" })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
          {(photos.data?.photos ?? []).length === 0 && (
            <li className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground sm:col-span-2 lg:col-span-3">
              Sin fotos en este estado.
            </li>
          )}
        </ul>
      )}
    </main>
  );
}
